/*
  G&H branded asset, created 2026-09-23 for Turbo 9.4.0.
  Loaded only by sections/header-branded.liquid when the ticker is enabled.

  <gh-ticker> is a custom element so it self-initializes wherever its markup
  lands: initial load, InstantClick body swaps (settings.performance =
  ludicrous), and theme editor section re-renders (shopify:section:load), with
  no changes to the stock Turbo JS. It measures one set of messages, clones
  the set until the track covers the viewport plus one loop, and drives a CSS
  keyframe animation through --ticker-loop-width and --ticker-duration.
*/
(function () {
  'use strict';

  if (window.customElements === undefined || window.customElements.get('gh-ticker')) return;

  var MAX_CLONES = 40;
  var MIN_LOOP_WIDTH = 1;

  class GHTicker extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ticker-track]');
      if (!this.track) return;

      this.originals = Array.prototype.slice.call(this.track.children).filter(function (el) {
        return !el.hasAttribute('data-ticker-clone');
      });
      if (!this.originals.length) return;

      this.speed = Math.max(5, parseFloat(this.getAttribute('data-speed')) || 60);
      this.reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.lastLoopWidth = 0;
      this.lastViewportWidth = 0;
      this.frame = null;

      this.onResize = this.schedule.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onBlockDeselect = this.handleBlockDeselect.bind(this);

      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(this.onResize);
        this.observer.observe(this);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.build();

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.onResize);
      }

      if (this.classList.contains('top-bar-ticker--mobile')) {
        document.body.classList.add('has-mobile-ticker');
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.classList.contains('top-bar-ticker--mobile') && !document.querySelector('.top-bar-ticker--mobile')) {
        document.body.classList.remove('has-mobile-ticker');
      }
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(function () {
        this.frame = null;
        this.build();
      }.bind(this));
    }

    removeClones() {
      Array.prototype.slice.call(this.track.querySelectorAll('[data-ticker-clone]')).forEach(function (el) {
        el.parentNode.removeChild(el);
      });
    }

    measureLoopWidth() {
      var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      var width = 0;
      this.originals.forEach(function (el) {
        width += el.getBoundingClientRect().width;
      });
      // one gap after every item, including the last, so sets tile seamlessly
      return width + gap * this.originals.length;
    }

    build() {
      if (!this.isConnected || !this.track) return;

      var viewportWidth = this.getBoundingClientRect().width;
      if (viewportWidth <= 0) return;

      this.classList.remove('is-ready');
      this.removeClones();

      var loopWidth = this.measureLoopWidth();
      if (loopWidth < MIN_LOOP_WIDTH) return;

      if (this.reducedMotion) {
        // No motion: show the first messages statically, nothing to clone.
        this.style.setProperty('--ticker-loop-width', '0px');
        return;
      }

      // Total sets needed so the track still covers the viewport after moving
      // one loop width: ceil(viewport / loop) + 1 sets, i.e. that many minus one clones.
      var sets = Math.ceil(viewportWidth / loopWidth) + 1;
      var clones = Math.min(MAX_CLONES, Math.max(1, sets - 1));
      var fragment = document.createDocumentFragment();
      for (var i = 0; i < clones; i++) {
        this.originals.forEach(function (el) {
          var clone = el.cloneNode(true);
          clone.setAttribute('data-ticker-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-shopify-editor-block');
          Array.prototype.slice.call(clone.querySelectorAll('a')).forEach(function (a) {
            a.setAttribute('tabindex', '-1');
          });
          fragment.appendChild(clone);
        });
      }
      this.track.appendChild(fragment);

      var duration = loopWidth / this.speed;
      this.style.setProperty('--ticker-loop-width', loopWidth.toFixed(2) + 'px');
      this.style.setProperty('--ticker-duration', duration.toFixed(2) + 's');
      this.lastLoopWidth = loopWidth;
      this.lastViewportWidth = viewportWidth;

      // Force a reflow so the animation restarts from the new values.
      void this.track.offsetWidth;
      this.classList.add('is-ready');
    }

    handleBlockSelect(event) {
      if (this.contains(event.target)) this.classList.add('is-paused');
    }

    handleBlockDeselect(event) {
      if (this.contains(event.target)) this.classList.remove('is-paused');
    }
  }

  window.customElements.define('gh-ticker', GHTicker);
})();
